/* eslint-disable arrow-body-style */
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import Modal from '../Modal';
import Button from '../Button';
import RenderStaticHTML from '../RenderStaticHTML';
import renderIcon from '../../utils/render-icon';
import { validateEmail } from '../../utils';

const ns = `oos-modal`;

const OOSModal = ({
	variant,
	isOOSModalOpen = false,
	onRequestClose,
}) => {
	const rootClassnames = classNames({
		[`${ ns }`]: true,
	});

	const defaultState = {
		email: '',
		addToEmailList: false,
		emailError: false,
		responseError: '',
	};

	const [success, setSuccess] = useState(false);
	const [formValues, setFormValues] = useState(defaultState);

	const close = () => {
		if (onRequestClose && typeof onRequestClose === 'function') {
			onRequestClose();
		}
	};

	const handleChange = event => {
		setFormValues(prev => {
			return {
				...prev,
				[event.target.name]: event.target.value,
				emailError: false,
				responseError: '',
			};
		});
	};

	const emailSubscription = () => {
		const options = {
			method: 'POST',
			headers: {
				revision: '2024-02-15',
				'content-type': 'application/json',
			},
			body: JSON.stringify({
				data: {
					type: 'subscription',
					attributes: {
						custom_source: 'Out of Stock form',
						profile: {
							data: {
								type: 'profile',
								attributes: {
									email: formValues.email,
								},
							},
						},
					},
					relationships: {list: {data: {type: 'list', id: process.env.GATSBY_KLAVIYO_NEWSLETTER_LIST_ID }}},
				},
			}),
		};

		fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${process.env.GATSBY_KLAVIYO_COMPANY_ID}`, options)
			.then(response => {
				if (!response.ok) {
					throw new Error('There was a network issue when subscribing');
				}
			})
			.catch(error => {
				setFormValues({
					...formValues,
					responseError: error.message,
				});
			});
	};

	const handleSubmit = e => {
		e.preventDefault();

		if (formValues.emailError || !validateEmail(formValues.email)) {
			setFormValues({ ...formValues, emailError: true });
			return;
		}

		fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${process.env.GATSBY_KLAVIYO_COMPANY_ID}`, {
			method: 'POST',
			headers: {
				accept: 'application/json',
				revision: '2024-02-15',
				'content-type': 'application/json',
			},
			body: JSON.stringify({
				data: {
					type: 'back-in-stock-subscription',
					attributes: {
						channels: ['EMAIL'],
						profile: {
							data: {
								type: 'profile',
								attributes: {
									email: formValues.email,
								},
							},
						},
					},
					relationships: {
						variant: {
							data: {
								type: 'catalog-variant',
								id: `$shopify:::$default:::${variant.shopifyId.split('/').pop()}`,
							},
						},
					},
				},
			}),
		}).then(response => {
			if (!response.ok) {
				throw new Error('There was a network issue when subscribing');
			}
			if (formValues.addToEmailList) {
				emailSubscription();
			}
			setSuccess(true);
		}).catch(error => {
			setFormValues({
				...formValues,
				responseError: error.message,
			});
		});
	};

	const verifyEmail = () => {
		setFormValues({
			...formValues,
			emailError: !validateEmail(formValues.email),
		});
	};

	const content = {
		title: 'Email me when this item is back in stock',
		text: 'Enter your email below and we’ll notify you if and when this item is made available for purchase.',
		invalidEmail: 'Invalid email address.',
		legal: 'This is not a guarantee that this product will become available for purchase again. Receiving a back in stock notification does not guarantee availability. <a href="/privacy">Privacy Policy</a>',
		success: 'Thanks for subscribing!',
		success_msg: 'Check your email for a confirmation message.',
	};

	useEffect(() => {
		if (!isOOSModalOpen) {
			setTimeout(() => {
				setSuccess(false);
				setFormValues(defaultState);
			}, 1000);
		}
	}, [isOOSModalOpen]);

	return (
		<Modal
			modifier={rootClassnames}
			isOpen={isOOSModalOpen}
			onRequestClose={close}
		>
			<div className={`${ ns }__container`}>
				<h2 className={`${ ns }__title h3`}>{success ? content.success : content.title}</h2>
				<p className={`${ ns }__text`}>{success ? content.success_msg : content.text}</p>
				{!success && (
					<>
						<form
							action={''}
							onSubmit={handleSubmit}
						>
							<input
								type={'email'}
								name={'email'}
								required
								onBlur={() => verifyEmail()}
								placeholder={'Email'}
								autoComplete={'email'}
								onChange={handleChange}
							/>
							{formValues.emailError && <span className={`${ ns }__invalid`}>{content.invalidEmail}</span>}
							{formValues.responseError && <span className={`${ ns }__invalid`}>{formValues.responseError}</span>}
							<div className={`${ ns }__checkbox`}>
								<input
									className={`visually-hidden`}
									type={'checkbox'}
									value={formValues.addToEmailList}
									onChange={e => { setFormValues({ ...formValues, addToEmailList: e.target.checked }); }}
								/>
								<label htmlFor={'marketing'}>
									<span className={`${ formValues.addToEmailList ? 'checked' : '' }`} aria-hidden>
										{renderIcon('checkbox')}
									</span>
									<div>Add me to Nordic Naturals email list</div>
								</label>
							</div>
							<Button
								variant={`yellow`}
								type={'submit'}
							>
								Submit
							</Button>
						</form>
						<RenderStaticHTML className={`${ ns }__legal`} html={content.legal} />
					</>
				)}
			</div>
		</Modal>
	);
};

export default OOSModal;
